import React, { useId, useState } from 'react';
import requiredIf from 'react-required-if';
import { Panel, PanelGroup } from 'react-resizable-panels';
import PropTypes from 'prop-types';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import FormGroup from '@mui/material/FormGroup';
import _ from 'lodash';

import { useStyles } from '~/assets/styles';
import { useInterval } from '~/components/AiChat/hooks/useInterval';
import ButtonGroup from '~/components/core/Atomic/ButtonGroup';
import Button from '~/components/core/Atomic/Buttons/Button';
import Chip from '~/components/core/Atomic/Chip/Chip';
import Grid from '~/components/core/Atomic/Grid/Grid';
import Tooltip from '~/components/core/Atomic/Tooltip';
import Typography from '~/components/core/Atomic/Typography';
import EmptyState from '~/components/core/EmptyState';
import { useMinimizedDialogs } from '~/components/core/MinimizedBar/Context';
import AutoComplete from '~/components/core/Molecules/Fields/AutoComplete';
import TextField from '~/components/core/Molecules/Fields/TextField';
import SidePanelCardDialog from '~/components/core/SidePanelCardDialog/SidePanelCardDialog';
import Text from '~/components/core/TextComponents/Text';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import DocumentMoreActionsContainer from '~/components/Documents/DocumentMoreActions/DocumentMoreActionsContainer';
import GenericInvoiceAnalysisContainer from '~/components/Documents/GenericInvoiceAnalysisContainer';
import { isInvoiceDamageAssessment } from '~/components/Documents/utils/damageAssessmentBaseType';
import InvoiceIcon from '~/components/icons/InvoiceIcon';
import SignatureIcon from '~/components/icons/SignatureIcon';
import Checkbox from '~/components/ui/atoms/Checkbox';
import Radio from '~/components/ui/atoms/Radio';
import useDataFetcher from '~/components/useDataFetcher';
import { isoDateToUs, serverDateTimeToLocal } from '~/DateTimeUtils';
import { MIXPANEL_EVENT_SOURCES, MIXPANEL_EVENTS } from '~/pocs/mixpanel';
import LEGACY_ORG_FEATURES from '~/server_shared/generated-types/LEGACY_ORG_FEATURES';
import { DocumentSignatureRequestsResponseESignStatus } from '~/server_shared/openapi/data-contracts';
import { CONFIGURATION_FEATURES_NAMES } from '~/Types';
import {
  getExposuresArrayFromClaimByIds,
  getExposuresLabels,
  isFeatureEnabled,
  isLegacyOrgFeatureEnabled,
  isPolicyNotFound,
  reportErrorInProductionOrThrow,
} from '~/Utils';
import { getSubOrganizationId, isClaimWriteDisabled } from '~/Utils/ClaimUtils';
import cn from '~/Utils/cn';

import { useClaim } from '../ClaimContainer';
import { CliveStoreProcessingStatus, CliveStoreStatusChip } from '../Clive/StatusChip/CliveStoreStatusChip';
import mixpanel from '../CmsMain/mixpanel';
import { SendDocumentViaVendorCommunication } from '../communications/VendorCommunicationCard';
import {
  PERMISSION_ACTIONS,
  PERMISSION_VERBS,
  PermissionsButtonWrapper,
  RestrictedPermissions,
  SortableTable,
} from '../core';
import SkeletonTable from '../core/Skeletons/SkeletonTable';
import ExposureLabelChips from '../ExposureLabelChips';
import ExposuresLabelFilter from '../exposures/ExposuresLabelsFilter';
import { getUserRelatedExposuresId } from '../exposures/ExposureUtils';
import { useCms } from '../hooks/useCms';
import useCoverageConfigs from '../hooks/useCoverageConfigs';
import useOrganization from '../OrganizationContext';
import { useCliveClaimContext } from '../SystemConfiguration/CliveConfiguration/CliveClaimContext';

import DocumentSidePanel from './DocumentSidePanel/DocumentSidePanel';
import NewSignatureRequestCard from './ESign/NewSignatureRequestCard';
import PostSignatureRequestCard from './ESign/PostSignatureRequestCard';
import { useSigningUrl } from './ESign/useSigningUrl';
import { ESIGN_STATUS_CONFIG } from './ESign/utils';
import { useDocumentProgress } from './hooks/useDocumentProgress';
import UploadClaimDocumentsWithClive from './UploadDocumentsCard/UploadClaimDocumentsWithClive';
import { getAllowedDocumentTypes } from './utils/documentTypes';
import CliveDocumentActionChip from './CliveDocumentActionChip';
import { getMixpanelAttributesWithDocument, isDocumentAPhoto } from './DocumentUtils';
import UploadDocumentFromTemplate from './UploadDocumentFromTemplate';
import ViewDocumentButton from './ViewDocumentButton';

const UPLOADING_DOCUMENTS_POLLING_INTERVAL_MS = 5000;

// Tracking functions
export const trackDocumentSummaryViewed = (document, claim, exposures, openedFrom = 'documents_click') => {
  const fullSummary = document.clive_store_information?.full_summary || '';
  mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_SUMMARY_VIEWED, {
    ...getMixpanelAttributesWithDocument(mixpanel, claim, document),
    full_summary_length: fullSummary.length,
    opened_from: openedFrom,
  });
};

export const trackDocumentSummaryClosed = (document, claim, exposures, viewTimeMs, closeMethod = 'close') => {
  mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_SUMMARY_CLOSED, {
    ...getMixpanelAttributesWithDocument(mixpanel, claim, document),
    view_time_ms: viewTimeMs,
    view_time_seconds: Math.round(viewTimeMs / 1000),
    close_method: closeMethod,
    summary_type: 'document_side_panel',
  });
};

const getDocumentsTableColumnData = (
  documentTypesDict,
  userOrganization,
  showAddToChat,
  effectiveWithCliveStatus,
  effectiveWithInvoiceAnalysis,
  setGenericInvoiceAnalysisDocument,
  isESignEnabled = false,
  signatureStatuses = {},
  setSignatureDialogDocument,
  isSignatureError,
  disableSignatureActions = false,
  handleExistingSignatureClick,
  isLoadingSigningUrl = false,
  documentProgressByKey = new Map()
) => {
  return [
    {
      id: 'id',
      numeric: true,
      rightPaddingOnly: true,
      label: '#',
      // eslint-disable-next-line react/display-name
      specialCell: (document) => document.claim_internal_id || null,
    },
    // eslint-disable-next-line react/display-name
    {
      id: 'view_action',
      label: 'Watch File',
      leftPaddingOnly: true,
      disableSort: true,
      disableRowSelectionEvent: true,
      specialCell: (document) => (
        <div className="flex justify-center p-0">
          <ViewDocumentButton document={document} />
        </div>
      ),
    },
    // eslint-disable-next-line react/display-name
    {
      id: 'exposure_ids',
      numeric: false,
      disablePadding: false,
      label: 'Label',
      specialCell: (document) =>
        document.is_uploading ? null : <ExposureLabelChips claimObject={document} disablePadding />,
    },
    {
      id: 'type',
      numeric: false,
      nowrap: true,
      disablePadding: true,
      label: 'Type',
      specialCell: (document) =>
        document.is_uploading ? null : documentTypesDict?.[document.type]?.desc || document.type,
    },
    {
      id: 'document_name',
      numeric: false,
      maxWidthPercentage: 16,
      overflow: true,
      disablePadding: false,
      label: 'Document Name',
    },
    {
      id: 'summary',
      numeric: false,
      maxWidthPercentage: 16,
      overflow: true,
      disablePadding: false,
      label: 'Summary',
      specialCell: (document) => (document.is_uploading ? null : document.summary),
    },
    {
      id: 'document_date',
      width: 135,
      numeric: false,
      disablePadding: true,
      label: 'Document Date',
      specialCell: (document) => (document.is_uploading ? null : isoDateToUs(document.document_date)),
    },
    {
      id: 'date_received',
      width: 135,
      numeric: false,
      disablePadding: true,
      label: 'Date Received',
      specialCell: (document) =>
        document.is_uploading || !document.date_received ? null : isoDateToUs(document.date_received),
    },
    {
      id: 'uploaded_by',
      numeric: false,
      disablePadding: true,
      label: 'Uploaded by',
      specialCell: (document) =>
        document.is_uploading ? null : document?.document_extra?.created_by_ai ? 'Clive' : document.uploaded_by,
    },
    {
      id: 'datetime_uploaded',
      width: 160,
      numeric: false,
      disablePadding: false,
      label: 'Uploaded Time',
      specialCell: (document) => (document.is_uploading ? null : serverDateTimeToLocal(document.datetime_uploaded)),
    },
    effectiveWithCliveStatus && {
      id: 'clive_analysis',
      numeric: false,
      overflow: false, // Disable overflow to remove wrapper divs
      disablePadding: false,
      label: "Clive's Analysis",
      specialCell: (document) => {
        // The live timeline knows a run failed before CMS has processed the failure webhook.
        const liveProgress = documentProgressByKey.get(document.ai_progress_key);
        if (
          liveProgress?.isFailed &&
          document?.clive_store_information?.processing_status === CliveStoreProcessingStatus.PROCESSING
        ) {
          return (
            <CliveStoreStatusChip status={CliveStoreProcessingStatus.ERROR} hasDocument={!document?.is_uploading} />
          );
        }

        const cliveActions = document.clive_actions || [];
        // Get non-dismissed CliveActions
        const nonDismissedActions = cliveActions.filter((a) => a.status !== 'dismissed');

        // If any clive actions exist, show them instead of CliveStoreStatusChip
        if (!_.isEmpty(nonDismissedActions)) {
          return (
            <div className="flex items-center gap-4">
              {nonDismissedActions.map((action) => (
                <CliveDocumentActionChip key={action.id} document={document} action={action} />
              ))}
            </div>
          );
        }

        // No clive actions - show the store status chip
        // Derive ANALYZING status: SUCCESS + pending deep analysis → show "Analyzing"
        let displayStatus = document?.clive_store_information?.processing_status;
        if (
          isFeatureEnabled(userOrganization, CONFIGURATION_FEATURES_NAMES.CLIVE_DEEP_ANALYSIS_STATUS) &&
          displayStatus === CliveStoreProcessingStatus.SUCCESS &&
          document?.clive_store_information?.deep_analysis_status === 'pending'
        ) {
          displayStatus = CliveStoreProcessingStatus.ANALYZING;
        }

        return <CliveStoreStatusChip status={displayStatus} hasDocument={!document?.is_uploading} />;
      },
    },
    effectiveWithInvoiceAnalysis && {
      id: 'invoice_analysis',
      numeric: false,
      overflow: false,
      disablePadding: false,
      label: 'Analysis',
      specialCell: (document) => {
        if (document.is_uploading) return null;

        const hasInvoice = document.invoice_id !== null;

        return (
          <div>
            {!hasInvoice ? (
              <Chip
                icon={<AddIcon />}
                variant="outlined"
                label="Add"
                onClick={() => setGenericInvoiceAnalysisDocument(document)}
                size="small"
              />
            ) : (
              <Chip
                icon={<InvoiceIcon style={{ width: 12, height: 16 }} />}
                variant="outlined"
                label="View"
                onClick={() => setGenericInvoiceAnalysisDocument(document)}
                size="small"
              />
            )}
          </div>
        );
      },
    },
    isESignEnabled && {
      id: 'esign_status',
      numeric: false,
      overflow: false,
      disablePadding: false,
      disableSort: true,
      disableRowSelectionEvent: true,
      label: 'Signature',
      specialCell: (document) => {
        if (isSignatureError) {
          return (
            <Chip
              icon={<SignatureIcon className="h-16 w-16" />}
              variant="outlined"
              label="N/A"
              size="small"
              className="w-full font-medium text-blue-700"
              disabled={true}
            />
          );
        }
        if (document.is_uploading) {
          return null;
        }

        const signatureInfo = signatureStatuses[document.id];

        if (signatureInfo) {
          const config = ESIGN_STATUS_CONFIG[signatureInfo.status];
          if (config) {
            const isClickableStatus = ![
              DocumentSignatureRequestsResponseESignStatus.COMPLETED,
              DocumentSignatureRequestsResponseESignStatus.CANCELLED,
              DocumentSignatureRequestsResponseESignStatus.ERROR,
            ].includes(signatureInfo.status);
            const shouldBeClickable = isClickableStatus && !disableSignatureActions && !isLoadingSigningUrl;

            return (
              <Chip
                icon={<SignatureIcon className="h-16 w-16" />}
                label={config.label}
                size="small"
                variant="outlined"
                className={cn('w-full font-medium', config.className, shouldBeClickable && 'cursor-pointer')}
                onClick={
                  shouldBeClickable
                    ? () =>
                        handleExistingSignatureClick(signatureInfo.request_id, document.id, signatureInfo.contact_id)
                    : undefined
                }
                disabled={isLoadingSigningUrl || disableSignatureActions}
              />
            );
          }
          reportErrorInProductionOrThrow(`Missing ESIGN_STATUS_CONFIG for status: ${signatureInfo.status}`);
          return null;
        }

        return (
          <Chip
            icon={<SignatureIcon className="h-16 w-16" />}
            variant="outlined"
            label="Send"
            size="small"
            className="w-full font-medium text-blue-700"
            onClick={disableSignatureActions ? undefined : () => setSignatureDialogDocument(document)}
            disabled={disableSignatureActions}
          />
        );
      },
    },
    {
      id: 'more_actions',
      rightPaddingOnly: true,
      numeric: false,
      label: 'Actions',
      disableRowSelectionEvent: true,
      specialCell: (document) => <DocumentMoreActionsContainer document={document} showAddToChat={showAddToChat} />,
    },
    // eslint-disable-next-line react/display-name
    {
      id: 'send_to_vendor',
      isAction: true,
      disableSort: true,
      numeric: false,
      disablePadding: true,
      width: 20,
      specialCell: (document) =>
        document.is_uploading ? null : (
          <SendDocumentViaVendorCommunication
            document={document}
            displayIcon
            vendorNameDisplay="medlogix"
            sentIndicationKeyName="was_sent_to_medlogix"
            isDocumentRelevant={(document, claim) =>
              claim.exposures
                .filter((exposure) => document.exposure_ids.includes(exposure.id))
                .some((exposure) => ['coverage_pip', 'coverage_apip'].includes(exposure.coverage_type)) &&
              isLegacyOrgFeatureEnabled(userOrganization, LEGACY_ORG_FEATURES.INSHUR_US_MEDLOGIX)
            }
            getSubmitRoute={(claimId) => `/api/v1/claims/${claimId}/vendors/medlogix/send_document`}
          />
        ),
    },
  ].filter(Boolean);
};

const DocumentsTable = (props) => {
  const {
    documents,
    selectedDocumentIds,
    onDocumentsClick,
    disableChooseMultiple,
    tableColumns,
    defaultOrderColumn,
    maxHeight,
    autoPaginateRowsPerPage,
    shouldDisableRow,
    showAddToChat,
    onDocumentSelect,
    selectedDocumentId,
    effectiveWithCliveSidePanel,
    effectiveWithCliveStatus,
    effectiveWithInvoiceAnalysis,
    setGenericInvoiceAnalysisDocument,
    isSignatureError,
    isESignEnabled,
    signatureStatuses,
    setSignatureDialogDocument,
    disableSignatureActions,
    disableDocumentsWithExistingSignature,
    emptyStateComponent,
    liveProgressKeys = new Set(),
  } = props;
  const { classes } = useStyles();
  const { documentTypesDict } = useOrganization();
  const { userOrganization } = useCms();
  // One name per table: the single-select radios form one group (one Tab stop, arrow keys inside it),
  // and two tables on one page stay separate groups.
  const selectionGroupName = useId();

  // Helper to check if a document is a failed upload (still in uploading queue with error status)
  const isFailedUpload = (document) => {
    const status = document?.clive_store_information?.processing_status;
    return (
      document?.is_uploading &&
      (status === CliveStoreProcessingStatus.ERROR ||
        status === CliveStoreProcessingStatus.ERROR_UPLOADING ||
        status === CliveStoreProcessingStatus.ERROR_CLASSIFYING_DOCUMENT_TYPE ||
        status === CliveStoreProcessingStatus.ERROR_UNSUPPORTED_FILE_TYPE ||
        status === CliveStoreProcessingStatus.ERROR_FILE_TOO_LARGE)
    );
  };

  // Predicate to identify documents still processing (for tooltip and styling)
  // A row whose live timeline is streaming stays clickable, so its side panel can show the run.
  const isUploadingDocument = (document) => {
    return document?.is_uploading && !isFailedUpload(document) && !liveProgressKeys.has(document.ai_progress_key);
  };

  // Enhanced shouldDisableRow that disables uploading documents (except failed ones that need manual retry)
  const enhancedShouldDisableRow = (document) => {
    // Disable documents that are still processing, but allow failed uploads to be clicked
    if (isUploadingDocument(document)) {
      return { disable: true };
    }

    // Check if document already has a signature request
    if (disableDocumentsWithExistingSignature && signatureStatuses[document?.id]) {
      return {
        disable: true,
        tooltip: 'This document already has a signature request',
      };
    }

    // Fall back to original shouldDisableRow logic for regular documents
    return shouldDisableRow?.(document) || {};
  };

  // Get IDs of uploading documents to grey out their text (but not failed ones)
  const greyedOutRowsIds = documents.filter(isUploadingDocument).map((document) => document.id);

  const docSelectionColumn = {
    id: 'select_doc',
    disableSort: true,
    width: 20,
    disablePadding: true,
    numeric: false,
    specialCell: (document) => {
      if (document.is_uploading) return null;

      const { disable = false, tooltip } = enhancedShouldDisableRow(document);
      const Component = disableChooseMultiple ? (
        <Radio name={selectionGroupName} checked={selectedDocumentIds.includes(document.id)} disabled={disable} />
      ) : (
        <Checkbox checked={selectedDocumentIds.includes(document.id)} disabled={disable} />
      );

      return tooltip ? (
        <Tooltip title={tooltip} placement="top">
          <span>{Component}</span>
        </Tooltip>
      ) : (
        Component
      );
    }, // no need for onChange callback - clicking anywhere on the row select the row and mark the checkbox
  };

  let documentTableColumns =
    tableColumns ||
    getDocumentsTableColumnData(
      documentTypesDict,
      userOrganization,
      showAddToChat,
      effectiveWithCliveStatus,
      effectiveWithInvoiceAnalysis,
      setGenericInvoiceAnalysisDocument,
      isESignEnabled,
      signatureStatuses,
      setSignatureDialogDocument,
      isSignatureError,
      disableSignatureActions
    );
  documentTableColumns = onDocumentsClick ? [docSelectionColumn].concat(documentTableColumns) : documentTableColumns;

  const handleDocumentsClick = (documentId) => {
    // Ignore not uploaded documents
    const doc = documents.find((document) => document.id === documentId);

    // Check if the document can be clicked (failed uploads are allowed, processing uploads are not)
    const { disable = false } = enhancedShouldDisableRow(doc);
    if (disable) return;

    if (effectiveWithCliveSidePanel) {
      // Clive side panel mode: select document for side panel
      onDocumentSelect(documentId);
    } else {
      // Multiple selection mode: add/remove from selection
      if (disableChooseMultiple) {
        onDocumentsClick([documentId]);
      } else {
        // Add the documentId to the selected if not selected already, otherwise remove it from selected
        onDocumentsClick(
          selectedDocumentIds.includes(documentId)
            ? _.without(selectedDocumentIds, documentId)
            : [...selectedDocumentIds, documentId]
        );
      }
    }
  };

  const selectAll = () => {
    const allAllowedDocuments = documents.filter((document) => {
      return !enhancedShouldDisableRow(document)?.disable;
    });

    return onDocumentsClick(_.uniq(selectedDocumentIds.concat(allAllowedDocuments.map((doc) => doc.id))));
  };

  const selectNone = () => {
    return onDocumentsClick(
      _.difference(
        selectedDocumentIds,
        documents.map((doc) => doc.id)
      )
    );
  };

  return (
    <>
      {onDocumentsClick && !disableChooseMultiple && (
        <div className={classes.cardDivRow}>
          <Grid container justify="space-between">
            <Typography variant="subtitle1">
              <strong>{selectedDocumentIds.length}</strong>&nbsp; documents are selected
            </Typography>
            <ButtonGroup variant="text" size="small">
              <Button onClick={selectAll}>All</Button>
              <Button onClick={selectNone}>None</Button>
            </ButtonGroup>
          </Grid>
        </div>
      )}
      <SortableTable
        columns={documentTableColumns}
        defaultOrderColumn={defaultOrderColumn || documentTableColumns.findIndex((column) => column.id === 'id')} // If no defaultOrderColumn, it should be the #id column regardless if we add the select_doc column
        order="desc"
        rows={documents}
        selectedRowsIds={onDocumentSelect ? (selectedDocumentId ? [selectedDocumentId] : []) : selectedDocumentIds}
        onRowClick={onDocumentsClick || onDocumentSelect ? handleDocumentsClick : undefined}
        maxHeight={maxHeight}
        stickyHeader
        autoPaginateRowsPerPage={autoPaginateRowsPerPage}
        greyedOutRowsIds={greyedOutRowsIds}
        isRestrictedPredicate={isUploadingDocument}
        restrictedContentTooltipText="Clive is uploading the document."
        emptyStateComponent={emptyStateComponent}
      />
    </>
  );
};

DocumentsTable.propTypes = {
  documents: PropTypes.array.isRequired,
  liveProgressKeys: PropTypes.instanceOf(Set),
  selectedDocumentIds: requiredIf(PropTypes.array, (props) => props.onDocumentsClick),
  onDocumentsClick: PropTypes.func,
  disableChooseMultiple: PropTypes.bool,
  tableColumns: PropTypes.array,
  defaultOrderColumn: PropTypes.number,
  maxHeight: PropTypes.string,
  autoPaginateRowsPerPage: PropTypes.number,
  shouldDisableRow: PropTypes.func,
  showAddToChat: PropTypes.bool,
  onDocumentSelect: PropTypes.func,
  selectedDocumentId: PropTypes.number,
  effectiveWithCliveSidePanel: PropTypes.bool,
  effectiveWithCliveStatus: PropTypes.bool,
  effectiveWithInvoiceAnalysis: PropTypes.bool,
  setGenericInvoiceAnalysisDocument: PropTypes.func,
  isSignatureError: PropTypes.bool,
  isESignEnabled: PropTypes.bool,
  signatureStatuses: PropTypes.object,
  setSignatureDialogDocument: PropTypes.func,
  disableSignatureActions: PropTypes.bool,
  disableDocumentsWithExistingSignature: PropTypes.bool,
  emptyStateComponent: PropTypes.node,
};

function DocumentsContainer(props) {
  const {
    classes,
    claim,
    documentsFilter,
    selectedDocumentIds,
    onDocumentsClick,
    disableChooseMultiple,
    onNewDocumentCreated,
    newDocumentDefaultType,
    maxHeight,
    autoPaginateRowsPerPage,
    removeUploadedTime,
    hideFromTemplate,
    initialValues,
    selectOnly,
    disableExposuresFilter,
    shouldDisableRow,
    showAddToChat,
    withCliveStatus,
    withCliveSidePanel,
    withInvoiceAnalysis,
    disableSignatureActions,
    disableDocumentsWithExistingSignature,
    showEmptyState,
  } = props;
  const [newDocumentOpen, setNewDocumentOpen] = useState(false);
  const [shouldIncludePhotos, setShouldIncludePhotos] = useState(false);
  const [newDocumentFromTemplateOpen, setNewDocumentFromTemplateOpen] = useState(false);
  const [selectedDocumentId, setSelectedDocumentId] = useState(null);
  const [genericInvoiceAnalysisDocument, setGenericInvoiceAnalysisDocument] = useState(null);
  const [signatureDialogDocument, setSignatureDialogDocument] = useState(null);
  const [existingSignatureData, setExistingSignatureData] = useState(null);
  const { add } = useMinimizedDialogs();

  const { user, userOrganization } = useCms();
  const { isESignEnabled, documentTypesDict } = useOrganization();
  const { coverageConfigs = [] } = useCoverageConfigs();
  const { fetchSigningUrl, isLoading: isLoadingSigningUrl } = useSigningUrl();

  const [filteredExposuresIds, setFilteredExposuresIds] = useState(
    () => initialValues.exposure_ids || (!disableExposuresFilter ? getUserRelatedExposuresId(user, claim) : [])
  );
  const [filteredDocumentType, setFilteredDocumentType] = useState(initialValues.type || null);
  const [searchDocumentFilter, setSearchDocumentFilter] = useState(null);

  // Only the type is seeded into the upload dialog. `filteredExposuresIds` is a view filter
  // over the table — it can be cleared to nothing and it carries the claim-level pseudo-id —
  // so it is not a preselection, and the dialog keeps its own handling-adjuster default.
  let UploadDocumentInitialValues = {};

  // The type filter above keeps offering every type the claim's documents use, including
  // ones the organization has since disabled or restricted, so a seeded type has to be
  // checked against what the upload dialog will actually offer before it preselects it.
  const newDocumentType = filteredDocumentType || newDocumentDefaultType;
  if (newDocumentType && getAllowedDocumentTypes(documentTypesDict, claim.lob).includes(newDocumentType)) {
    UploadDocumentInitialValues['type'] = newDocumentType;
  }

  const handleSubmitDocument = (document, handleClose) => {
    // TODO: do some sort of "loading" mechanism between the time we close the dialog until the refreshed answer comes in
    onNewDocumentCreated(document.id);
    handleClose();
  };

  const handleCloseFromTemplate = () => setNewDocumentFromTemplateOpen(false);

  const readOnly = isClaimWriteDisabled(claim, user, { allowOnClosedClaim: true });

  let { documents, uploading_documents = [] } = claim;

  const { isCliveEnabled } = useCliveClaimContext();
  const subOrganizationId = getSubOrganizationId(claim);
  const isCliveEnabledForSubOrg = isCliveEnabled(subOrganizationId);

  const { refreshData: silentlyRefreshClaim } = useClaim();

  const documentProgressByKey = useDocumentProgress({
    claimId: claim.id,
    enabled: isCliveEnabledForSubOrg,
    onDocumentFinished: silentlyRefreshClaim,
  });

  // While Clive is still processing an uploaded document it lives in
  // claim.uploading_documents as a partial row whose action/status icons are
  // hidden. Nothing pushes an update when processing finishes, so poll a silent
  // claim refetch until the row transitions into claim.documents (mirrors
  // CommunicationAttachmentTable's attachment-processing poll).
  const hasProcessingUploadingDocuments =
    isCliveEnabledForSubOrg &&
    (claim.uploading_documents || []).some(
      (doc) => doc?.clive_store_information?.processing_status === CliveStoreProcessingStatus.PROCESSING
    );

  const { start: startUploadingDocumentsPolling, stop: stopUploadingDocumentsPolling } = useInterval(
    silentlyRefreshClaim,
    UPLOADING_DOCUMENTS_POLLING_INTERVAL_MS
  );

  React.useEffect(() => {
    if (hasProcessingUploadingDocuments && silentlyRefreshClaim) {
      startUploadingDocumentsPolling();
    } else {
      stopUploadingDocumentsPolling();
    }
    return () => {
      stopUploadingDocumentsPolling();
    };
  }, [
    hasProcessingUploadingDocuments,
    silentlyRefreshClaim,
    startUploadingDocumentsPolling,
    stopUploadingDocumentsPolling,
  ]);

  // Reverse uploading documents to show most recent first, then merge with regular documents
  // Add claim_id to uploading documents so they can be used in ViewDocumentButton
  const recentUploadingDocuments = isCliveEnabledForSubOrg
    ? [...uploading_documents].reverse().map((doc) => ({ ...doc, claim_id: claim.id }))
    : [];

  documents = [...recentUploadingDocuments, ...documents.filter((document) => !document.is_removed)];
  const claimHasDocuments = documents.length > 0;

  let documentTypes = _.uniq(documents.filter((doc) => !doc.is_uploading).map((doc) => doc.type));
  documentTypes.sort();

  if (documentsFilter) {
    documents = documents.filter(documentsFilter);
  }

  if (!shouldIncludePhotos) {
    documents = documents.filter((document) => !(isDocumentAPhoto(document) && document.type === 'damage_photo'));
  }

  if (filteredExposuresIds.length !== 0) {
    documents = documents.filter(
      (document) => document.is_uploading || _.intersection(filteredExposuresIds, document.exposure_ids).length > 0
    );
  }

  if (filteredDocumentType) {
    documents = documents.filter((document) => document.is_uploading || document.type === filteredDocumentType);
  }

  if (searchDocumentFilter) {
    documents = documents.filter((document) =>
      document.document_name.toLowerCase().includes(searchDocumentFilter.toLowerCase())
    );
  }

  const {
    data: signatureData,
    isLoading: isLoadingSignatures,
    isError: isSignatureError,
    reloadData: refetchSignatureData,
  } = useDataFetcher(
    `/api/v1/organizations/${userOrganization.id}/e_sign/signature_requests?claim_id=${claim.id}`,
    undefined,
    { shouldFetch: isESignEnabled && claim.documents.length > 0 }
  );

  const signatureStatuses = React.useMemo(() => {
    if (!signatureData?.signature_requests) {
      return {};
    }
    return signatureData.signature_requests.reduce((acc, req) => {
      acc[req.document_id] = { status: req.status, request_id: req.id, contact_id: req.contact_id };
      return acc;
    }, {});
  }, [signatureData]);

  const handleExistingSignatureClick = async (requestId, documentId, contactId) => {
    const signUrl = await fetchSigningUrl(requestId);
    if (signUrl) {
      const document = documents.find((d) => d.id === documentId);
      setExistingSignatureData({
        requestId,
        signUrl,
        documentId,
        document,
        contactId,
      });
    }
  };

  const effectiveWithCliveStatus = React.useMemo(() => {
    return isCliveEnabledForSubOrg && withCliveStatus;
  }, [isCliveEnabledForSubOrg, withCliveStatus]);

  const effectiveWithCliveSidePanel = React.useMemo(() => {
    return isCliveEnabledForSubOrg && withCliveSidePanel;
  }, [isCliveEnabledForSubOrg, withCliveSidePanel]);

  const hasGenericInvoiceDA = React.useMemo(() => {
    return coverageConfigs.some(isInvoiceDamageAssessment);
  }, [coverageConfigs]);

  const isCliveInvoiceActionNotificationsEnabled = isFeatureEnabled(
    userOrganization,
    CONFIGURATION_FEATURES_NAMES.CLIVE_INVOICE_ACTION_NOTIFICATIONS
  );

  const effectiveWithInvoiceAnalysis = React.useMemo(() => {
    // Show column if either: 1) Generic Invoice DA is configured, or 2) Clive Invoice Action Notifications FF is enabled
    return (hasGenericInvoiceDA && withInvoiceAnalysis) || isCliveInvoiceActionNotificationsEnabled;
  }, [hasGenericInvoiceDA, withInvoiceAnalysis, isCliveInvoiceActionNotificationsEnabled]);

  let tableColumns = getDocumentsTableColumnData(
    documentTypesDict,
    userOrganization,
    showAddToChat,
    effectiveWithCliveStatus,
    effectiveWithInvoiceAnalysis,
    setGenericInvoiceAnalysisDocument,
    isESignEnabled,
    signatureStatuses,
    setSignatureDialogDocument,
    isSignatureError,
    disableSignatureActions,
    handleExistingSignatureClick,
    isLoadingSigningUrl,
    documentProgressByKey
  );
  // If selecting documents is enabled, remove datetime_upload column to save space in the row for the checkbox
  if (removeUploadedTime || onDocumentsClick) {
    tableColumns = tableColumns.filter((column) => column.id !== 'datetime_uploaded');
  }
  if (selectOnly) {
    tableColumns = tableColumns.filter((column) => !column.isAction);
  }

  const exposures = getExposuresArrayFromClaimByIds(claim, filteredExposuresIds);

  const handleDocumentSelect = (documentId) => {
    setSelectedDocumentId(documentId === selectedDocumentId ? null : documentId);
  };

  const liveProgressKeys = React.useMemo(() => new Set(documentProgressByKey.keys()), [documentProgressByKey]);
  const selectedDocument = selectedDocumentId ? documents.find((doc) => doc.id === selectedDocumentId) : null;
  const selectedDocumentProgress = selectedDocument
    ? documentProgressByKey.get(selectedDocument.ai_progress_key)
    : undefined;

  // An uploading row is replaced by its document when processing ends; keep the side panel on it.
  const lastSelectedProgressKey = React.useRef(null);
  React.useEffect(() => {
    if (selectedDocument?.ai_progress_key) lastSelectedProgressKey.current = selectedDocument.ai_progress_key;
  }, [selectedDocument?.ai_progress_key]);
  React.useEffect(() => {
    if (!selectedDocumentId || selectedDocument || !lastSelectedProgressKey.current) return;
    const successor = documents.find((doc) => doc.ai_progress_key === lastSelectedProgressKey.current);
    if (successor) setSelectedDocumentId(successor.id);
  }, [selectedDocumentId, selectedDocument, documents]);

  // Use refs to maintain tracking state
  const trackingState = React.useRef({
    prevShouldShow: false,
    prevSelectedDocumentId: null,
    viewStartTime: null,
  });

  // Check if the side panel will actually render
  const shouldShowSidePanel = React.useMemo(() => {
    if (!effectiveWithCliveSidePanel || !selectedDocument?.clive_store_information) {
      return false;
    }

    const processingStatus = selectedDocument.clive_store_information.processing_status;

    // Show panel for all statuses EXCEPT PROCESSING, unless the live timeline has something to show
    return processingStatus !== CliveStoreProcessingStatus.PROCESSING || Boolean(selectedDocumentProgress);
  }, [effectiveWithCliveSidePanel, selectedDocument, selectedDocumentProgress]);

  // Handle tracking logic when shouldShowSidePanel or selectedDocumentId changes
  React.useEffect(() => {
    const state = trackingState.current;
    const prevShouldShow = state.prevShouldShow;
    const prevDocId = state.prevSelectedDocumentId;

    // First, handle close events (must come before open events for document switches)
    if (prevShouldShow && (!shouldShowSidePanel || (shouldShowSidePanel && prevDocId !== selectedDocumentId))) {
      // Panel closed OR switched to different document while panel was open
      if (state.viewStartTime) {
        const prevDocument = prevDocId ? documents.find((doc) => doc.id === prevDocId) : null;
        if (prevDocument) {
          trackDocumentSummaryClosed(
            prevDocument,
            claim,
            exposures,
            Date.now() - state.viewStartTime,
            !shouldShowSidePanel ? 'close' : 'document_switch'
          );
        }
        state.viewStartTime = null;
      }
    }

    // Then, handle open events
    if (shouldShowSidePanel && (!prevShouldShow || (prevShouldShow && prevDocId !== selectedDocumentId))) {
      // Panel opened OR switched to different document while panel was open
      if (selectedDocument) {
        trackDocumentSummaryViewed(
          selectedDocument,
          claim,
          exposures,
          !prevShouldShow ? 'documents_click' : 'document_switch'
        );
        state.viewStartTime = Date.now();
      }
    }

    // Update state for next render
    state.prevShouldShow = shouldShowSidePanel;
    state.prevSelectedDocumentId = selectedDocumentId;
  }, [shouldShowSidePanel, selectedDocumentId, selectedDocument, claim, exposures, documents]);

  const uploadDocumentFromTemplateProps = {
    classes,
    claim,
    onSubmitDocument: (document) => handleSubmitDocument(document, handleCloseFromTemplate),
    onCancel: handleCloseFromTemplate,
    open: true,
  };

  const handleClickFromTemplate = () => {
    mixpanel.track(MIXPANEL_EVENTS.DOCUMENT_TEMPLATE_OPENED, {
      ...mixpanel.getMixpanelAttributes(claim, exposures, {
        source: MIXPANEL_EVENT_SOURCES.DOCUMENT_TEMPLATE_OPENED_FROM_DOCUMENT_CONTAINER,
      }),
    });

    const type = 'DOCUMENT';
    const { handleCloseDialog } = add({
      barHeader: `New Document Template`,
      type,
      dialogComponent: UploadDocumentFromTemplate,
      dialogProps: {
        ...uploadDocumentFromTemplateProps,
        onMinimized: () => {
          mixpanel.track(MIXPANEL_EVENTS.MINIMIZED_DIALOG, {
            source: MIXPANEL_EVENT_SOURCES.DOCUMENT_TEMPLATE_OPENED_FROM_DOCUMENT_CONTAINER,
            type,
          });
        },
        onCancel: () => handleCloseDialog(),
        onSubmitDocument: (document) => {
          handleSubmitDocument(document, handleCloseDialog);
        },
      },
    });
  };

  return (
    <RestrictedPermissions action={PERMISSION_ACTIONS.CLAIM_DOCUMENT} verb={PERMISSION_VERBS.WRITE}>
      <Grid container alignItems="center" spacing={2}>
        <Grid item md={3}>
          <div className="mr-12 flex items-center">
            <AutoComplete
              id="document_type"
              label="Filter by Document Type"
              options={documentTypes}
              getOptionLabel={(option) => documentTypesDict?.[option]?.['desc']}
              className="w-[350px]"
              onChange={(_, newVal) => setFilteredDocumentType(newVal)}
              value={filteredDocumentType}
            />
          </div>
        </Grid>
        <Grid item md={3}>
          <div className="flex items-center">
            <TextField
              label="Search"
              style={{ width: 350 }}
              onChange={_.debounce((value) => setSearchDocumentFilter(value), 300)}
            />
          </div>
        </Grid>
        <Grid item md={2}>
          <div className="ml-20 mt-12 flex items-center">
            <Checkbox
              checked={shouldIncludePhotos}
              onChange={(_event, newValue) => newValue !== null && setShouldIncludePhotos(newValue)}
            />
            <Text variant={Text.VARIANTS.SM} weight={Text.WEIGHTS.REGULAR}>
              Show photos
            </Text>
          </div>
        </Grid>
        <Grid item md={4}>
          <FormGroup row>
            <div className={classes.spacer} />
            {!hideFromTemplate && !selectOnly && (
              <PermissionsButtonWrapper>
                <Button
                  color="primary"
                  disabled={readOnly || isPolicyNotFound(claim)}
                  onClick={handleClickFromTemplate}
                >
                  <AddIcon className={classes.leftButtonIcon} />
                  From Template
                </Button>
              </PermissionsButtonWrapper>
            )}
            {!selectOnly && (
              <PermissionsButtonWrapper>
                <Button color="primary" disabled={readOnly} onClick={() => setNewDocumentOpen(true)}>
                  <CloudUploadIcon className={classes.leftButtonIcon} />
                  Upload Documents
                </Button>
              </PermissionsButtonWrapper>
            )}
          </FormGroup>
        </Grid>
      </Grid>
      {!disableExposuresFilter && (
        <div style={{ padding: '10px 0' }}>
          <ExposuresLabelFilter filterList={filteredExposuresIds} onUpdateFiltered={setFilteredExposuresIds} />
        </div>
      )}
      <PanelGroup
        direction="horizontal"
        id="group-documents"
        autoSaveId="documents-panel-layout"
        style={{ height: 'initial' }}
      >
        <Panel id="documents-display" defaultSize={shouldShowSidePanel ? 60 : 100} style={{ overflow: 'auto' }}>
          {isESignEnabled && isLoadingSignatures ? (
            <SkeletonTable rowsCount={5} columnsCount={tableColumns.length} maxHeight={maxHeight} />
          ) : (
            <DocumentsTable
              classes={classes}
              documents={documents}
              selectedDocumentIds={selectedDocumentIds}
              onDocumentsClick={onDocumentsClick}
              disableChooseMultiple={disableChooseMultiple}
              maxHeight={maxHeight}
              autoPaginateRowsPerPage={autoPaginateRowsPerPage}
              tableColumns={tableColumns}
              shouldDisableRow={shouldDisableRow}
              showAddToChat={showAddToChat}
              onDocumentSelect={effectiveWithCliveSidePanel ? handleDocumentSelect : undefined}
              selectedDocumentId={selectedDocumentId}
              effectiveWithCliveSidePanel={effectiveWithCliveSidePanel}
              effectiveWithCliveStatus={effectiveWithCliveStatus}
              effectiveWithInvoiceAnalysis={effectiveWithInvoiceAnalysis}
              setGenericInvoiceAnalysisDocument={setGenericInvoiceAnalysisDocument}
              isSignatureError={isSignatureError}
              isESignEnabled={isESignEnabled}
              signatureStatuses={signatureStatuses}
              setSignatureDialogDocument={setSignatureDialogDocument}
              disableSignatureActions={props.disableSignatureActions}
              disableDocumentsWithExistingSignature={disableDocumentsWithExistingSignature}
              liveProgressKeys={liveProgressKeys}
              emptyStateComponent={
                showEmptyState ? (
                  <EmptyState
                    subtitle={
                      claimHasDocuments
                        ? 'No documents match the current filters.'
                        : 'No documents have been added to this claim yet.'
                    }
                    illustration={<></>}
                  />
                ) : undefined
              }
            />
          )}
        </Panel>

        {shouldShowSidePanel && (
          <SidePanelCardDialog panelId="document-display">
            <DocumentSidePanel
              document={selectedDocument}
              claim={claim}
              onClose={() => setSelectedDocumentId(null)}
              onDocumentUpdate={onNewDocumentCreated}
              progress={selectedDocumentProgress}
            />
          </SidePanelCardDialog>
        )}
      </PanelGroup>

      {newDocumentOpen && (
        <UploadClaimDocumentsWithClive
          classes={classes}
          claim={claim}
          onSubmitDocument={(document) => handleSubmitDocument(document, () => setNewDocumentOpen(false))}
          onCancel={() => setNewDocumentOpen(false)}
          onCliveUploadSuccess={() => {
            // Refresh the documents list after Clive upload
            onNewDocumentCreated(null);
          }}
          initialValues={UploadDocumentInitialValues}
        />
      )}
      {newDocumentFromTemplateOpen && <UploadDocumentFromTemplate {...uploadDocumentFromTemplateProps} />}
      {genericInvoiceAnalysisDocument ? (
        <GenericInvoiceAnalysisContainer
          open={!!genericInvoiceAnalysisDocument}
          document={genericInvoiceAnalysisDocument}
          onClose={() => setGenericInvoiceAnalysisDocument(null)}
        />
      ) : null}
      {signatureDialogDocument && (
        <NewSignatureRequestCard
          document={signatureDialogDocument}
          onCancel={() => setSignatureDialogDocument(null)}
          onSuccess={() => {
            setSignatureDialogDocument(null);
            refetchSignatureData();
          }}
        />
      )}
      {existingSignatureData && existingSignatureData.contactId && (
        <PostSignatureRequestCard
          contact={
            claim.contacts?.find((c) => c.id === existingSignatureData.contactId) || claim?.insured_contact || null
          }
          signUrl={existingSignatureData.signUrl}
          claimId={claim.id}
          onClose={() => setExistingSignatureData(null)}
        />
      )}
    </RestrictedPermissions>
  );
}

DocumentsContainer.propTypes = {
  classes: PropTypes.object.isRequired,
  claim: PropTypes.object.isRequired,
  documentsFilter: PropTypes.func,
  onNewDocumentCreated: PropTypes.func,
  selectedDocumentIds: requiredIf(PropTypes.array, (props) => props.onDocumentsClick),
  onDocumentsClick: PropTypes.func,
  disableChooseMultiple: PropTypes.bool,
  newDocumentDefaultType: PropTypes.string,
  removeUploadedTime: PropTypes.bool,
  maxHeight: PropTypes.string,
  autoPaginateRowsPerPage: PropTypes.number,
  hideFromTemplate: PropTypes.bool,
  initialValues: PropTypes.object,
  selectOnly: PropTypes.bool,
  disableExposuresFilter: PropTypes.bool,
  shouldDisableRow: PropTypes.func,
  showAddToChat: PropTypes.bool,
  withCliveStatus: PropTypes.bool,
  withCliveSidePanel: PropTypes.bool,
  withInvoiceAnalysis: PropTypes.bool,
  disableSignatureActions: PropTypes.bool,
  disableDocumentsWithExistingSignature: PropTypes.bool,
  // Explain an empty table instead of showing an empty grid; only the claim's Documents tab beside Requests asks.
  showEmptyState: PropTypes.bool,
};

DocumentsContainer.defaultProps = {
  initialValues: {},
  withCliveStatus: false,
  withCliveSidePanel: false,
  withInvoiceAnalysis: false,
};

export { DocumentsTable, getDocumentsTableColumnData, getExposuresLabels };
export default DocumentsContainer;
